.base {
	width: var(--parrot-chat-sidebar-width);
	flex-shrink: 0;
	background: var(--parrot-chat-surface);
	border-right: 1px solid var(--parrot-chat-border);
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: hidden;
}

.header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	height: var(--parrot-chat-header-height);
	padding: 0 10px 0 14px;
	flex-shrink: 0;
}

.logo {
	height: 16px;
	width: auto;
	max-width: 150px;
	object-fit: contain;
}

.brand {
	font-size: var(--parrot-chat-font-size-sm);
	font-weight: var(--parrot-chat-font-weight-medium);
	color: var(--parrot-chat-text-muted);
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.iconBtn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	flex-shrink: 0;
	padding: 0;
	background: transparent;
	border: none;
	border-radius: var(--parrot-chat-radius);
	color: var(--parrot-chat-text-muted);
	cursor: pointer;
	transition: color 0.12s, background 0.12s;
}

.iconBtn:hover {
	color: var(--parrot-chat-text);
	background: var(--parrot-chat-menu-hover);
}

.iconBtn svg {
	width: 18px;
	height: 18px;
	fill: none;
	stroke: currentColor;
	stroke-width: 2;
	stroke-linecap: round;
	stroke-linejoin: round;
}

/* Nav items ported from the CORE product sidebar: full-bleed rows with a left-edge
   "glider" indicator. The glider track anchors to this relatively-positioned container. */
.ctas {
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 0;
	padding: 6px 0 4px;
}

.cta {
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	height: 40px;
	padding: 0 12px 0 14px;
	position: relative;
	z-index: 1;
	background: transparent;
	border: none;
	border-radius: 0;
	color: var(--parrot-chat-text-secondary);
	font-family: inherit;
	font-size: var(--parrot-chat-font-size-sm);
	font-weight: var(--parrot-chat-font-weight-medium);
	text-align: left;
	white-space: nowrap;
	cursor: pointer;
	transition: color 0.15s, background 0.15s;
}

.cta:hover {
	color: var(--parrot-chat-text);
	background: var(--parrot-chat-menu-hover);
}

/* Active row (Search while open): accent colour only — the glow comes from the glider. */
.ctaActive {
	color: var(--parrot-chat-accent);
}

.ctaActive .cta svg,
.ctaActive svg {
	color: var(--parrot-chat-accent);
}

.ctaActive:hover {
	color: var(--parrot-chat-accent);
	background: color-mix(in srgb, var(--parrot-chat-accent) 10%, transparent);
}

.cta svg {
	width: 18px;
	height: 18px;
	flex-shrink: 0;
	fill: none;
	stroke: currentColor;
	stroke-width: 2;
	stroke-linecap: round;
	stroke-linejoin: round;
}

/* ── Glider track (static, faint vertical line on the left edge) ── */
.gliderTrack {
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	width: 2px;
	background: linear-gradient(
		0deg,
		transparent 0%,
		var(--parrot-chat-border) 50%,
		transparent 100%
	);
	pointer-events: none;
	z-index: 0;
}

/* ── Glider (moves to the hovered / active item) ── height + transform set inline. ── */
.glider {
	position: absolute;
	left: 0;
	top: 0;
	width: 2px;
	pointer-events: none;
	z-index: 2;
	transition:
		transform 0.5s cubic-bezier(0.37, 1.95, 0.66, 0.56),
		opacity 0.2s ease;
	background: linear-gradient(
		0deg,
		transparent 0%,
		var(--parrot-chat-accent) 50%,
		transparent 100%
	);
}

/* Bloom — soft glow spreading horizontally from the bar. */
.glider::before {
	content: "";
	position: absolute;
	height: 55%;
	width: 300%;
	top: 50%;
	transform: translateY(-50%);
	background: var(--parrot-chat-accent);
	filter: blur(9px);
	opacity: 0.8;
}

/* Wash — accent light fading into the row. */
.glider::after {
	content: "";
	position: absolute;
	left: 0;
	top: 0;
	height: 100%;
	width: 80px;
	background: linear-gradient(
		90deg,
		color-mix(in srgb, var(--parrot-chat-accent) 15%, transparent) 0%,
		transparent 100%
	);
}

.searchRow {
	padding: 2px 12px 6px;
}

.search {
	width: 100%;
	box-sizing: border-box;
	padding: 8px 10px;
	background: var(--parrot-chat-bg);
	border: 1px solid var(--parrot-chat-border-strong);
	border-radius: var(--parrot-chat-radius);
	color: var(--parrot-chat-text);
	font-family: inherit;
	font-size: var(--parrot-chat-font-size-sm);
	outline: none;
	transition: border-color 0.15s, box-shadow 0.15s;
}

.search:focus {
	border-color: var(--parrot-chat-accent);
	box-shadow: 0 0 0 3px var(--parrot-chat-accent-glow);
}

.search::placeholder {
	color: var(--parrot-chat-text-muted);
}

.list {
	flex: 1;
	overflow-y: auto;
	padding: 4px 8px 8px;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.group {
	display: flex;
	flex-direction: column;
	gap: 2px;
	margin-bottom: 6px;
}

.groupLabel {
	font-size: 10px;
	font-weight: var(--parrot-chat-font-weight-medium);
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: var(--parrot-chat-text-muted);
	padding: 8px 8px 4px;
}

.empty {
	color: var(--parrot-chat-text-muted);
	font-size: 12px;
	padding: 12px 10px;
	text-align: center;
}
